/**
 * The configurator, the practice panel and the deployment record.
 *
 * These three are the spine of the home page in the design, and the configurator is the
 * argument it is making: *this is what your counter costs, worked out in front of you,
 * from the same price table our written quotes come from*. It is not a marketing widget
 * — the totals are real, and "Send this configuration" posts the whole specification as
 * an enquiry so the office can quote against exactly what the customer saw.
 */

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney, friendlyError } from '@serendib/shared';
import { api } from '../lib/api.js';
import { trackLead } from '../lib/tracking.js';
import { useCounter } from '../lib/stores.js';
import { directionsLink, setting, useBootstrap, whatsappLink, type ProjectCard } from '../lib/queries.js';
import { Field, Honeypot } from '../components/ui.jsx';
import { CoverArtwork } from '../components/Artwork.jsx';
import { panelClass } from './frame.js';

type P = Record<string, any>;

/** A heading whose newlines are honoured — the design breaks two of them deliberately. */
export function BrokenHeading({ text, style }: { text: string; style?: React.CSSProperties }) {
  const lines = text.split('\n');
  return (
    <h2 style={style}>
      {lines.map((line, i) => (
        <span key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </span>
      ))}
    </h2>
  );
}

/* ========================================================================== */
/* Configurator                                                               */
/* ========================================================================== */

interface Choice {
  value: string;
  label: string;
  price: number;
  perLane: boolean;
  billLabel: string | null;
}
interface Option {
  key: string;
  label: string;
  note: string | null;
  /** The choice selected before anyone touches it; falls back to the first. */
  defaultValue: string | null;
  choices: Choice[];
}

export function Configurator({ payload }: { payload: P }) {
  const options: Option[] = payload.options ?? [];
  const { choices, lanes, setChoice, setLanes, seed } = useCounter();
  const { data: bootstrap } = useBootstrap();
  const currency = setting(bootstrap, 'store.currency', 'LKR');

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');

  // Seed the store from the payload's first choice per option, once. `seed` merges
  // rather than overwrites, so a re-render cannot discard what the visitor picked.
  useEffect(() => {
    if (options.length === 0) return;
    seed(
      Object.fromEntries(
        options.map((o) => {
          const named = o.defaultValue && o.choices.some((c) => c.value === o.defaultValue) ? o.defaultValue : null;
          return [o.key, named ?? o.choices[0]?.value ?? ''];
        }),
      ),
    );
  }, [options, seed]);

  /**
   * The bill of materials and the total.
   *
   * Arithmetic in one place, derived from the current selection — there is no second
   * copy of the total that could disagree with the lines above it. Zero-priced choices
   * ("Skip") produce no line at all rather than a `Rs 0` row, because a bill listing
   * the things you did not buy is longer and says less.
   */
  const { bom, total } = useMemo(() => {
    const rows: { label: string; amount: number }[] = [];

    for (const option of options) {
      const chosen = option.choices.find((c) => c.value === choices[option.key]);
      if (!chosen || chosen.price <= 0 || !chosen.billLabel) continue;
      const quantity = chosen.perLane ? lanes : 1;
      rows.push({
        label: quantity > 1 ? `${chosen.billLabel} × ${quantity}` : chosen.billLabel,
        amount: chosen.price * quantity,
      });
    }

    const install = Number(payload.installBase ?? 0) + Number(payload.installPerLane ?? 0) * lanes;
    if (install > 0) rows.push({ label: payload.installLabel ?? 'Install, cabling and training', amount: install });

    return { bom: rows, total: rows.reduce((sum, r) => sum + r.amount, 0) };
  }, [options, choices, lanes, payload.installBase, payload.installPerLane, payload.installLabel]);

  const money = (n: number) => formatMoney(n.toFixed(2), currency, { decimals: false });

  /**
   * The enquiry has to carry whoever built it.
   *
   * This used to post the configuration under the company's OWN contact address, with
   * "Counter configuration" where the sender's name goes. Every quote therefore arrived
   * looking like it had been sent by the office to itself: nobody to reply to, nothing
   * to match against a phone call, and "Reply by email" in the back office answering
   * our own inbox. The specification was perfect and entirely anonymous.
   *
   * So the three fields that make it answerable are asked for here, and the browser
   * validates them before anything is sent. Name and email are required because a quote
   * that cannot be delivered is not worth taking; the telephone is not, because plenty
   * of operators would rather be called than written to and plenty would not.
   */
  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSending(true);
    setError(null);
    setFieldErrors({});
    try {
      // The whole specification goes in the message, in words. The office quotes
      // against what the customer actually saw, not against a set of option codes
      // somebody then has to decode.
      const spec = options
        .map((o) => `${o.label}: ${o.choices.find((c) => c.value === choices[o.key])?.label ?? '—'}`)
        .concat(`Number of counters: ${lanes}`)
        .concat('', 'Bill of materials:')
        .concat(bom.map((r) => `  ${r.label} — ${money(r.amount)}`))
        .concat('', `Estimated price, without VAT: ${money(total)}`)
        .join('\n');

      await api.post(
        '/public/leads',
        {
          name: String(form.get('name') ?? ''),
          email: String(form.get('email') ?? ''),
          phone: form.get('phone') ? String(form.get('phone')) : null,
          message: ['Counter configuration', '', spec].join('\n'),
          source: 'QUOTE',
          website: honeypot,
        },
        { auth: false },
      );
      // Lead fires only for a real enquiry, never for a newsletter signup: campaigns B
      // and C optimise toward this event, and diluting it with mailing-list additions
      // would teach Meta to find people who subscribe rather than people who buy.
      trackLead('QUOTE');
      setSent(true);
    } catch (err) {
      setError(friendlyError(err));
      const errors = (err as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (errors) setFieldErrors(errors);
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="section wrap" id="configure">
      <div className={panelClass({ ground: payload.ground })}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 28, flexWrap: 'wrap' }}>
          <div>
            {payload.eyebrow ? <p className="eyebrow">{payload.eyebrow}</p> : null}
            {payload.heading ? (
              <h2 style={{ marginTop: 10 }}>
                {payload.heading}
              </h2>
            ) : null}
          </div>
          {payload.note ? (
            <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ink-2)', maxWidth: '38ch' }}>{payload.note}</p>
          ) : null}
        </div>

        <div className="cfg-grid" style={{ marginTop: 32 }}>
          {/* -- Choices ------------------------------------------------- */}
          <div style={{ display: 'grid', gap: 10 }}>
            {options.map((option) => (
              <div key={option.key} className="cfg-row">
                <div>
                  <p style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.02em' }}>{option.label}</p>
                  {option.note ? (
                    <p style={{ marginTop: 5, fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink-2)', maxWidth: '50ch' }}>
                      {option.note}
                    </p>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {option.choices.map((choice) => {
                    const picked = choices[option.key] === choice.value;
                    return (
                      <button
                        key={choice.value}
                        type="button"
                        onClick={() => setChoice(option.key, choice.value)}
                        aria-pressed={picked}
                        className={`cfg-pick ${picked ? 'is-picked' : ''}`}
                      >
                        {choice.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="cfg-row">
              <div>
                <p style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.02em' }}>How many counters?</p>
                <p style={{ marginTop: 5, fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink-2)' }}>
                  Each extra counter needs the same hardware and adds half a day of work.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', background: 'var(--panel)', borderRadius: 999, padding: 4, justifySelf: 'end' }}>
                <button type="button" className="cfg-step" onClick={() => setLanes(lanes - 1)} disabled={lanes <= 1} aria-label="One fewer counter">−</button>
                <span style={{ fontSize: 16.5, fontWeight: 600, minWidth: 44, textAlign: 'center' }} aria-live="polite">{lanes}</span>
                <button type="button" className="cfg-step" onClick={() => setLanes(lanes + 1)} disabled={lanes >= (payload.maxLanes ?? 8)} aria-label="One more counter">+</button>
              </div>
            </div>
          </div>

          {/* -- Bill of materials --------------------------------------- */}
          <div className="cfg-bom">
            <p className="eyebrow">Bill of materials</p>

            <div style={{ marginTop: 18, display: 'grid', gap: 2 }}>
              {bom.map((row) => (
                <div key={row.label} className="cfg-bom-row">
                  <span style={{ color: 'var(--ink-2)' }}>{row.label}</span>
                  <span className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--ink)' }}>{money(row.amount)}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 18, background: 'var(--accent)', borderRadius: 20, padding: '20px 22px' }}>
              <p style={{ fontSize: 13, color: 'var(--accent-ink)', opacity: 0.85 }}>{payload.totalLabel ?? 'Estimated price, without VAT'}</p>
              <p style={{ marginTop: 6, fontSize: 'var(--t-figure-sm)', fontWeight: 700, letterSpacing: '-0.035em', whiteSpace: 'nowrap', color: 'var(--accent-ink)' }}>
                {money(total)}
              </p>
            </div>

            <div style={{ marginTop: 16, display: 'grid', gap: 9, fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              <p>Fitting takes {Math.max(1, Math.ceil(lanes / 2))} night{Math.ceil(lanes / 2) === 1 ? '' : 's'} in your shop</p>
              <p>First working system: {payload.buildWeeks ?? 6} weeks after you approve</p>
            </div>

          </div>
        </div>

        {/*
          The quote request is a step AFTER the counter is built, so it sits under both
          columns rather than inside the bill. Two things come of that: the options
          column and the bill now end within ~70px of each other instead of 270px
          apart, and the three fields read as one last action across the foot of the
          section instead of a form tucked into the corner of a sticky panel.
        */}
        {sent ? (
          <p className="cfg-sent" role="status">Sent. We will send you a price for exactly this.</p>
        ) : (
          <form className="cfg-band" onSubmit={send}>
            <div className="cfg-band__ask">
              <p className="cfg-band__title">{payload.contactNote ?? 'Where should we send the price?'}</p>
              <p className="cfg-band__note">We reply in one working day.</p>
            </div>

            <div className="cfg-band__fields">
              <Honeypot value={honeypot} onChange={setHoneypot} />
              <Field label="Your name" required error={fieldErrors.name}>
                <input className="input" name="name" required autoComplete="name" placeholder="e.g. Priyantha Perera" />
              </Field>
              <Field label="Email" required error={fieldErrors.email}>
                <input className="input" name="email" type="email" required autoComplete="email" placeholder="you@company.lk" />
              </Field>
              <Field label="Phone number" error={fieldErrors.phone}>
                <input className="input" name="phone" type="tel" autoComplete="tel" placeholder="011 330 40 48" />
              </Field>
              <button type="submit" className="cfg-send" disabled={sending}>
                {sending ? 'Sending…' : payload.ctaLabel ?? 'Send this configuration'}
              </button>
            </div>
          </form>
        )}
        {error ? <p className="field-error" style={{ marginTop: 12 }} role="alert">{error}</p> : null}
      </div>
    </section>
  );
}

/* ========================================================================== */
/* Practice                                                                   */
/* ========================================================================== */

export function Practice({ payload }: { payload: P }) {
  const items: { title: string; body: string }[] = payload.items ?? [];
  const facts: { value: string; label: string }[] = payload.facts ?? [];

  return (
    <section className="section wrap" id="practice">
      <div className="panel">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <div>
            {payload.eyebrow ? <p className="eyebrow">{payload.eyebrow}</p> : null}
            {payload.heading ? (
              <BrokenHeading
                text={payload.heading}
                style={{ marginTop: 12 }}
              />
            ) : null}
          </div>
          {payload.body ? (
            <p style={{ fontSize: 16.5, lineHeight: 1.6, color: 'var(--ink-2)', maxWidth: '46ch', textWrap: 'pretty' }}>
              {payload.body}
            </p>
          ) : null}
        </div>

        <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
          {items.map((item, index) => (
            <div key={item.title} style={{ background: 'var(--wash)', borderRadius: 24, padding: '28px 28px 30px' }}>
              <p className="badge-num">{String(index + 1).padStart(2, '0')}</p>
              <h3 style={{ marginTop: 16, fontSize: 19.5, letterSpacing: '-0.025em', fontWeight: 600, lineHeight: 1.28 }}>{item.title}</h3>
              <p style={{ marginTop: 11, fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-2)', textWrap: 'pretty' }}>{item.body}</p>
            </div>
          ))}
        </div>

        {facts.length > 0 ? (
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            {facts.map((fact) => (
              <div key={fact.label} style={{ background: 'var(--wash)', borderRadius: 24, padding: '26px 28px' }}>
                <p style={{ fontSize: 'var(--t-figure)', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1, color: 'var(--accent-deep)' }}>{fact.value}</p>
                <p style={{ marginTop: 8, fontSize: 14.5, color: 'var(--ink-2)' }}>{fact.label}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ========================================================================== */
/* Deployment record                                                          */
/* ========================================================================== */

export function DeploymentRecord({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const project = resolved as ProjectCard | null;

  // The panel prefers the project's own words over the payload's, so editing the case
  // study updates the home page. The payload carries only what the project record has
  // no field for — the timeline, and the photograph's caption.
  const heading = payload.heading ?? project?.title ?? null;
  const body = payload.body ?? project?.summary ?? null;
  const timeline: { when: string; what: string }[] = payload.timeline ?? [];
  const mediaUrl = payload.media?.url ?? project?.coverImageUrl ?? null;
  const href = payload.ctaHref ?? (project ? `/projects/${project.slug}` : null);

  if (!heading) return null;

  return (
    <section className="section wrap" id="deployment">
      <div className={panelClass({ ground: payload.ground })}>
        <div className="deploy-grid">
          <div>
            {payload.eyebrow ? <p className="eyebrow">{payload.eyebrow}</p> : null}
            <h2 style={{ marginTop: 12 }}>
              {heading}
            </h2>
            {body ? (
              <p style={{ marginTop: 16, fontSize: 16.5, lineHeight: 1.6, color: 'var(--ink-2)', maxWidth: '42ch', textWrap: 'pretty' }}>{body}</p>
            ) : null}

            <div className="deploy-media">
              {mediaUrl ? (
                <img src={mediaUrl} alt={payload.media?.alt ?? project?.coverImageAlt ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <CoverArtwork seed={project?.slug ?? heading} label={heading} />
              )}
            </div>
          </div>

          <div>
            {timeline.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {timeline.map((entry, i) => (
                  <div key={`${entry.when}-${i}`} className="deploy-step">
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-deep)' }}>{entry.when}</span>
                    <span style={{ fontSize: 15.5, lineHeight: 1.55, color: '#2C3238' }}>{entry.what}</span>
                  </div>
                ))}
              </div>
            ) : null}

            {payload.quote ? (
              <figure style={{ margin: '26px 0 0', background: 'rgba(1,124,102,0.07)', borderRadius: 26, padding: '32px 34px' }}>
                <blockquote style={{ fontSize: 20, lineHeight: 1.55, letterSpacing: '-0.02em', maxWidth: '52ch', textWrap: 'pretty' }}>
                  “{payload.quote}”
                </blockquote>
                {payload.quoteAttribution ? (
                  <figcaption style={{ marginTop: 16, fontSize: 14.5, color: 'var(--ink-2)' }}>{payload.quoteAttribution}</figcaption>
                ) : null}
              </figure>
            ) : null}

            {href && payload.ctaLabel ? (
              <Link to={href} className="btn btn--secondary" style={{ marginTop: 22 }}>
                {payload.ctaLabel}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================== */
/* Contact                                                                    */
/* ========================================================================== */

export function ContactSplit({ payload }: { payload: P }) {
  const { data: bootstrap } = useBootstrap();
  const [need, setNeed] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');

  const needs: string[] = payload.needsOptions ?? [];

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState('sending');
    setError(null);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    try {
      await api.post(
        '/public/leads',
        {
          name: String(form.get('name') ?? ''),
          email: String(form.get('email') ?? ''),
          phone: form.get('phone') ? String(form.get('phone')) : null,
          company: form.get('business') ? String(form.get('business')) : null,
          serviceInterest: need,
          message: String(form.get('message') ?? ''),
          source: payload.source ?? 'CONTACT',
          website: honeypot,
        },
        { auth: false },
      );
      trackLead(payload.source ?? 'CONTACT');
      setState('sent');
    } catch (err) {
      setState('idle');
      setError(friendlyError(err));
      const errors = (err as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (errors) setFieldErrors(errors);
    }
  }

  const dark = payload.tone === 'dark';
  const whatsapp = whatsappLink(bootstrap);
  const directions = directionsLink(bootstrap);
  const mapEmbed = setting(bootstrap, 'contact.map_embed_url', '');

  const cards: { label: string; body: ReactNode }[] = [];
  if (payload.showOffice !== false) {
    cards.push({
      label: 'Office',
      body: (
        <>
          {setting(bootstrap, 'contact.address_line1', '')}
          <br />
          {setting(bootstrap, 'contact.address_line2', '')}
          {/* The page invites people to visit, so it has to say where. A link rather than
              an embedded map: nothing reaches Google until somebody wants directions. */}
          {directions ? (
            <>
              <br />
              <a href={directions} target="_blank" rel="noreferrer noopener" className="small" style={{ color: 'var(--accent-deep)', display: 'inline-block', marginTop: 8 }}>
                Get directions ↗
              </a>
            </>
          ) : null}
        </>
      ),
    });
  }
  if (payload.showPhone !== false) {
    const phone = setting(bootstrap, 'contact.phone', '');
    cards.push({
      // The label followed the WhatsApp number rather than announcing one that was never
      // there: it read "Telephone & WhatsApp" while offering no way to reach WhatsApp.
      label: whatsapp ? 'Telephone & WhatsApp' : 'Telephone',
      body: (
        <>
          <a href={`tel:${setting(bootstrap, 'contact.phone_link', phone)}`} style={{ color: 'var(--ink)', fontSize: 19, fontWeight: 600 }}>{phone}</a>
          {whatsapp ? (
            <>
              <br />
              <a href={whatsapp} target="_blank" rel="noreferrer noopener" className="small" style={{ color: 'var(--accent-deep)', display: 'inline-block', marginTop: 8 }}>
                Message on WhatsApp ↗
              </a>
            </>
          ) : null}
        </>
      ),
    });
  }
  if (payload.showEmail !== false) {
    const email = setting(bootstrap, 'contact.email', '');
    cards.push({ label: 'Email', body: <a href={`mailto:${email}`} style={{ color: 'var(--ink)' }}>{email}</a> });
  }

  return (
    // `on-dark` re-points the ink, panel and accent tokens (tokens.css), so the form,
    // the cards and every link inside invert correctly without any of them knowing they
    // are on a dark ground.
    <section className={`section wrap${dark ? ' on-dark' : ''}`} id="contact">
      <div className="contact-grid">
        <div className={panelClass({ ground: payload.ground, dark })}>
          {payload.eyebrow ? <p className="eyebrow">{payload.eyebrow}</p> : null}
          {payload.heading ? (
            <h2 style={{ marginTop: 10 }}>{payload.heading}</h2>
          ) : null}
          {payload.body ? (
            <p style={{ marginTop: 12, fontSize: 16.5, lineHeight: 1.55, color: 'var(--ink-2)', maxWidth: '50ch', textWrap: 'pretty' }}>{payload.body}</p>
          ) : null}

          {state === 'sent' ? (
            <div style={{ marginTop: 30, background: 'var(--accent-wash)', borderRadius: 22, padding: 'var(--pad-card)', display: 'grid', gap: 8 }} role="status">
              <p style={{ fontSize: 19, fontWeight: 600, color: 'var(--accent-deep)' }}>Thank you</p>
              <p style={{ color: 'var(--ink-2)' }}>{payload.successMessage ?? 'We have your brief and will reply within one business day.'}</p>
            </div>
          ) : (
            <form onSubmit={submit} style={{ marginTop: 30, display: 'grid', gap: 18 }} noValidate>
              <div aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="cs-website">Leave this empty</label>
                <input id="cs-website" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>

              <div className="pair">
                <Field label="Name" required error={fieldErrors.name}>
                  <input className="input" name="name" required autoComplete="name" placeholder="Priyantha Jayasundera" />
                </Field>
                <Field label="Business" error={fieldErrors.company}>
                  <input className="input" name="business" autoComplete="organization" placeholder="Ceylon Merchants Ltd" />
                </Field>
              </div>

              <div className="pair">
                <Field label="Phone" error={fieldErrors.phone}>
                  <input className="input" name="phone" type="tel" autoComplete="tel" placeholder="+94 77 123 4567" />
                </Field>
                <Field label="Email" required error={fieldErrors.email}>
                  <input className="input" name="email" type="email" required autoComplete="email" placeholder="director@company.lk" />
                </Field>
              </div>

              {needs.length > 0 ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  <span className="label">What do you need?</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {needs.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setNeed(need === option ? null : option)}
                        aria-pressed={need === option}
                        className={`cfg-pick ${need === option ? 'is-picked' : ''}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <Field label="Tell us more" required error={fieldErrors.message}>
                <textarea className="textarea" name="message" rows={5} required placeholder="Two counters, stock in two shops, and we still write bills by hand…" />
              </Field>

              {error ? <p className="field-error" role="alert">{error}</p> : null}

              <div className="row">
                <button className="btn btn--primary" type="submit" disabled={state === 'sending'}>
                  {state === 'sending' ? 'Sending…' : payload.submitLabel ?? 'Send the brief'}
                </button>
                {payload.footnote ? <p className="xs dim" style={{ maxWidth: '32ch' }}>{payload.footnote}</p> : null}
              </div>
            </form>
          )}
        </div>

        {/* With a map configured this column fills the row and the map takes whatever
            height the cards leave; without one there is only ~250px of content, so it
            travels with the form instead (see `.contact-aside` in base.css). */}
        <div className={`contact-aside${mapEmbed ? ' contact-aside--map' : ''}`}>
          {cards.map((card) => (
            <div key={card.label} className={`panel${dark ? ' panel--dark' : ''}`} style={{ padding: 'var(--pad-card)' }}>
              <p className="xs dim" style={{ letterSpacing: '0.04em' }}>{card.label}</p>
              <div style={{ marginTop: 8, fontSize: 16, lineHeight: 1.55, color: 'var(--ink)' }}>{card.body}</div>
            </div>
          ))}

          {/* Opt-in only. An embedded map hands every visitor's IP to Google on page load
              whether or not they care where the office is, so it appears only when the
              client has deliberately pasted an embed URL in Settings. `loading="lazy"`
              keeps it off the critical path even then. */}
          {mapEmbed ? (
            <div className="panel panel--flush contact-map">
              <iframe
                src={mapEmbed}
                title="Map showing the Serendib IT Solutions office"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
